import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { isEmpty, reject } from 'lodash';
import { Button, Drawer, Typography } from 'antd';
import Text from 'antd/lib/typography/Text';
import RoleTag from './RoleTag';
import DF from 'DisplayFramework';
import DataGrid from 'components/DataGrid';
import RouterLink from 'components/RouterLink';
import { DataGridColDef } from 'components/DataGrid/DataGrid';
import TasksListPopover from '../workflow/TasksListPopover';
import {
  findByTpReferenceAndTpName,
  FindByTpReferenceAndTpNameParams,
  TpList,
} from 'services/thirdParty';
import GetLabel from 'DisplayFramework/components/GetLabel';
import ThirdPartyModal from './ThirdPartyDetail/ThirdPartyModal';
import ThirdPartyInput, {
  ThirdPartyForProposalType,
} from '../../components/ThirdPartyInput';
import SynthesList from 'pages/Configuration/ParSynthesObject/SynthesList';
import IActions from 'icons/IActions';
import ButtonComponent from 'components/Button';
import { PlusOutlined } from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
import ParListSelect from 'components/ParListSelect';

const INITIAL_SEARCH_PARAMS: FindByTpReferenceAndTpNameParams = {
  parentid: 0,
  role: '',
  tpfiscalid: '',
  tpname: '',
  tpreference: '',
  tpcardid: '',
  tptype: '',
};

export type ThirdPartySearchProps = {
  isModal?: string;
  initialSearchParams?: Partial<FindByTpReferenceAndTpNameParams>;
  onSelect?: (item: TpList) => void;
  allowCreate?: boolean;
  isProposalCreate?: boolean;
  setThirdPartyForProposal?: (
    ThirdPartyForProposal: ThirdPartyForProposalType
  ) => void;
  parcodeRole?: string;
  hideThirdPartyModal?: () => void;
  hideFilters?: boolean;
  sameTpid?: number;
};

const ThirdPartySearch = ({
  initialSearchParams,
  onSelect,
  allowCreate,
  isModal,
  parcodeRole,
  isProposalCreate,
  setThirdPartyForProposal,
  hideThirdPartyModal,
  hideFilters,
  sameTpid,
}: ThirdPartySearchProps) => {
  const [searchs, setsearchs] = useState<any>(false);
  const [searchParams, setSearchParams] =
    useState<FindByTpReferenceAndTpNameParams>({
      ...INITIAL_SEARCH_PARAMS,
      ...initialSearchParams,
    });

  const history = useHistory();

  const [visible, setVisible] = useState(false);
  const [tpid, settpid] = useState<number>();
  const [currcode, setCurrcode] = useState<string>();

  const closeSynthes = () => {
    setVisible(false);
  };
  const openDrawerSynthes = (tpid: number, currcode: string) => {
    settpid(tpid);
    setCurrcode(currcode);

    setVisible(true);
  };
  const {
    data: thirdParties,
    refetch,
    isFetching,
  } = useQuery(
    ['findByTpReferenceAndTpName', searchParams],
    () =>
      findByTpReferenceAndTpName(searchParams)
        .then((res) => {
          // Add test on response code
          if (res.code !== '200') {
            throw new Error('Service responded with an error');
          }
          // Filter out items with tpid === sametpid
          return (res.list || []).filter((item) => item.tpid !== sameTpid);
        })
        .catch(() => {
          throw new Error('Service not available');
        }),
    { enabled: searchs, refetchOnMount: false }
  );

  const [tpModal, setTpModal] = useState(false);
  const [newtpid] = useState(0);
  const [clear, setClear] = useState(false);

  const columns: DataGridColDef<TpList>[] = [
    {
      dataIndex: 'tpreference',
      render: (tpreference, record) =>
        onSelect ? (
          <Typography.Link onClick={() => onSelect(record)}>
            {tpreference}
          </Typography.Link>
        ) : (
          <RouterLink to={`/third-party/${record.tpid}/TP`}>
            {tpreference}
          </RouterLink>
        ),
      fixed: 'left',
      width: '10%',
      filtrable: true,
    },
    {
      dataIndex: 'tpname',
      fixed: 'left',
      width: '15%',
      filtrable: true,
    },
    {
      dataIndex: 'tptype',
      fixed: 'left',
      width: '15%',
      filtrable: true,
      filterComponent: <ParListSelect parcode="TPTYPE" />,
      render: (value, record) => {
        if (value === 'PM') {
          return <Text> Company </Text>;
        } else {
          return <Text> Individual </Text>;
        }
      },
    },
    {
      dataIndex: 'parentreference',
      render: (parentreference, record) =>
        !onSelect ? (
          <RouterLink to={`/third-party/${record.parentid}/TP`}>
            {parentreference}
          </RouterLink>
        ) : (
          parentreference
        ),
      fixed: 'left',
      width: '10%',
      filtrable: false,
    },
    {
      comcode: 'parentname',
      dataIndex: 'parentid',
      fixed: 'left',
      width: '15%',
      filtrable: true,
      filterComponent: <ThirdPartyInput onlytpid clear={clear}/>,
      render: (tpid, record) => record.parentname,
    },
    {
      dataIndex: 'tagroles',
      width: '10%',

      filtrable: 'role',
      filterComponent: !parcodeRole ? (
        <ParListSelect
          parcode="TPMAINROLE"
          value={initialSearchParams?.role ? initialSearchParams?.role : ''}
          disabled={initialSearchParams?.role === 'CLIENT' && isProposalCreate}
          style={{ width: '100%' }}
        ></ParListSelect>
      ) : (
        <ParListSelect
          parcode={parcodeRole ? parcodeRole : 'TPMAINROLE'}
          disabled={parcodeRole ? false : true}
          style={{ width: '100%' }}
        />
      ),

      render: (tagroles) => (
        <>
          {reject(tagroles.split(','), isEmpty).map((role) => (
            <RoleTag role={role} key={role} />
          ))}
        </>
      ),
    },
    {
      dataIndex: 'tpmainadrstreet1',
      width: '20%',
    },
    {
      dataIndex: 'tpmainadrcity',
      width: '15%',
    },
    {
      dataIndex: 'tpfiscalid',
      width: '20%',
    },
    {
      width: '10%',
      comcode: 'synthes',
      dataIndex: 'synthesis',
      fixed: 'right',
      render: (text, record) => (
        <Button
          type="primary"
          size="small"
          onClick={() => openDrawerSynthes(record.tpid, record.currcode)}
        >
          Open Synthes
        </Button>
      ),
    },
    {
      comcode: 'actions',
      dataIndex: 'operation',
      fixed: 'right',
      width: '10%',
      render: (text, record) =>
        isModal !== 'true' && (
          <TasksListPopover
            clientName={record.tpname}
            entityId={record.tpid}
            entityReference={record.tpreference}
            entityName="THIRDPARTY"
            onClose={refetch}
            tptype={record.tptype}
            title={record.tpreference + ' - ' + record.tpname}
            currcode={record.currcode}
          >
            <Button size="small" icon={<IActions height="15px" width="15px" />}>
              {' '}
              <GetLabel label="actions" />
            </Button>
          </TasksListPopover>
        ),
    },
  ];

  return (
    <DF.Provider pagcode="tplist">
      <DataGrid
        titleComcode="pageTitleList"
        columns={
          !isModal
            ? columns
            : columns.filter(
                (ea) =>
                  ea.dataIndex !== 'operation' &&
                  ea.dataIndex !== 'synthesis' &&
                  ea.dataIndex !== 'tpfiscalid'
              )
        }
        dataSource={thirdParties}
        rowKey="tpid"
        isLoading={isFetching}
        onSearch={(values) => {
          setSearchParams(values);
          setsearchs(true);
          refetch();
        }}
        onClear={() => {
         
          setClear(true)
        }}
        initialSearchValues={{
          ...INITIAL_SEARCH_PARAMS,
          ...initialSearchParams,
        }}
        componentName="tp_list"
        scroll={{ x: 1000, y: isModal ? 200 : 450 }}
        pagination={{ pageSize: 50 }}
        hideExtraAction={isModal ? true : false}
        hideFilters={hideFilters ? true : false}
        extraActions={
          isModal !== 'true' && (
            <ButtonComponent
              onClick={() => history.push('/third-party/create')}
              icon={<PlusOutlined />}
              type="primary"
              comcode="add"
            />
          )
        }
      />
      <Drawer
        title={<GetLabel label="synthesList" />}
        width={400}
        onClose={closeSynthes}
        visible={visible}
        bodyStyle={{ paddingBottom: 80, alignContent: 'center' }}
        style={{
          right: '0px',
          bottom: '0px',
          width: '100%',
          padding: '10px 16px',
        }}
      >
        <SynthesList
          objecttype="THIRDPARTY"
          objectid={tpid || 0}
          currcode={currcode || (localStorage.getItem('currcode') ?? '')}
        />
      </Drawer>
      <ThirdPartyModal
        title="newx"
        tpId={newtpid}
        visible={tpModal}
        onCancel={() => setTpModal(false)}
        isProposalCreate
        setThirdPartyForProposal={setThirdPartyForProposal}
        hideThirdPartySearchModal={() => setTpModal(false)}
        hideThirdPartyModal={hideThirdPartyModal}
      />
    </DF.Provider>
  );
};

export default ThirdPartySearch;
